import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, ListChecks, Loader2, MoreVertical, RefreshCw, Search, X } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import { User, Campaign } from '../../../../types';
import type { UserRolePatch } from '../../../../domain/users/repository';
import { ConfirmDialog } from '../../../../components/TaskShell/ConfirmDialog';
import { PortalMenu } from '../../../../components/PortalMenu';
import { SearchableDropdown } from '../../../../components/SearchableDropdown';
import { buildBrowseTasksSearchParams } from '../../../BrowseTasksPage/filter';
import { useUsersToolViewModel } from './viewModel';
import { USERS_FILTER_TABS, UsersFilterTab } from './filter';
import { paths } from '../../../../routes/paths';
import { AiDetectionWarningPill } from '../../../../components/AiDetectionWarningPill';

export function UsersTool({ mobileNavTrigger }: { mobileNavTrigger?: React.ReactNode }) {
  const { currentUser } = useAuth();
  const vm = useUsersToolViewModel();
  useViewModelReady(vm.initialize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => vm.dispose(), []);

  const campaignFilterMissing = vm.tab === 'campaign' && !vm.campaignFilterId;

  return (
    <section className="min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Users</h1>
          <p className="text-sm text-gray-500 mt-1">
            Grant admin privileges or assign reviewers to campaigns. Reviewer assignments on
            Aligned campaigns are mirrored to Aligned for linked experts.
          </p>
        </div>
        {mobileNavTrigger}
      </div>

      {vm.error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
          {vm.error}
        </div>
      )}

      {vm.notice && (
        <div
          role="status"
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="flex-1">{vm.notice}</p>
          <button
            type="button"
            onClick={vm.dismissNotice}
            aria-label="Dismiss notice"
            className="rounded p-0.5 text-amber-700 hover:bg-amber-100"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      )}

      {vm.busy && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {!vm.busy && (
        <>
          <FilterBar
            query={vm.query}
            onQueryChange={vm.setQuery}
            tab={vm.tab}
            onTabChange={vm.setTab}
            campaigns={vm.campaigns}
            campaignFilterId={vm.campaignFilterId}
            onCampaignFilterIdChange={vm.setCampaignFilterId}
            totalCount={vm.totalCount}
            paging={vm.paging}
          />

          <div className="mt-4 relative bg-white border border-gray-200 rounded-lg overflow-hidden">
            {vm.paging && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">User</th>
                    <th className="px-3 py-2 font-medium">Roles</th>
                    <th className="px-3 py-2 font-medium">Review campaigns</th>
                    <th className="px-3 py-2 font-medium">Trusted campaigns</th>
                    <th className="w-10 px-2 py-2" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vm.pageUsers.map((user) => (
                    <UserRow
                      key={user.uid}
                      user={user}
                      campaigns={vm.campaigns}
                      isSelf={currentUser?.uid === user.uid}
                      pending={vm.pendingUid === user.uid}
                      syncingUnsubscribe={vm.syncingUnsubscribeUid === user.uid}
                      onUpdate={(patch) => vm.updateRoles(user.uid, patch)}
                      onSyncUnsubscribeToken={() => vm.syncUnsubscribeToken(user.uid)}
                    />
                  ))}
                  {vm.pageUsers.length === 0 && !vm.paging && (
                    <tr>
                      <td colSpan={5} className="p-6 text-sm text-gray-500">
                        {campaignFilterMissing
                          ? 'Pick a campaign to see its reviewers.'
                          : 'No users match the current filters.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            page={vm.page}
            totalPages={vm.totalPages}
            totalCount={vm.totalCount}
            pageSize={vm.pageSize}
            currentPageSize={vm.pageUsers.length}
            onNext={vm.nextPage}
            onPrev={vm.prevPage}
          />
        </>
      )}
    </section>
  );
}

interface FilterBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  tab: UsersFilterTab;
  onTabChange: (t: UsersFilterTab) => void;
  campaigns: Campaign[];
  campaignFilterId: string | null;
  onCampaignFilterIdChange: (id: string | null) => void;
  totalCount: number | null;
  paging: boolean;
}

function FilterBar({
  query,
  onQueryChange,
  tab,
  onTabChange,
  campaigns,
  campaignFilterId,
  onCampaignFilterIdChange,
  totalCount,
  paging,
}: FilterBarProps) {
  const sortedCampaigns = useMemo(
    () => [...campaigns].sort((a, b) => a.name.localeCompare(b.name)),
    [campaigns],
  );
  return (
    <div className="mt-6 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:min-w-[240px] sm:max-w-md sm:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search name or email…"
            aria-label="Search users by name or email"
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div
          className="grid w-full grid-cols-4 rounded-md border border-gray-300 bg-white p-0.5 text-sm sm:inline-flex sm:w-auto"
          role="tablist"
          aria-label="Filter users by role"
        >
          {USERS_FILTER_TABS.map((spec) => {
            const active = tab === spec.id;
            return (
              <button
                key={spec.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabChange(spec.id)}
                className={`px-3 py-1.5 rounded transition-colors ${
                  active ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {spec.label}
              </button>
            );
          })}
        </div>

        <div className="w-full text-xs text-gray-500 sm:ml-auto sm:w-auto">
          {paging && totalCount === null
            ? 'Counting…'
            : totalCount === null
            ? ''
            : `${totalCount} ${totalCount === 1 ? 'user' : 'users'}`}
        </div>
      </div>

      {tab === 'campaign' && (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="users-campaign-filter" className="text-xs text-gray-600">
            Campaign
          </label>
          <SearchableDropdown
            id="users-campaign-filter"
            options={[
              { value: '', label: 'Select a campaign…' },
              ...sortedCampaigns.map((campaign) => ({
                value: campaign.id,
                label: campaign.name,
                searchText: `${campaign.name} ${campaign.id}`,
              })),
            ]}
            value={campaignFilterId ?? ''}
            onChange={(value) => onCampaignFilterIdChange(value || null)}
            placeholder="Select a campaign…"
            searchPlaceholder="Search campaigns"
            preserveOrder
            className="min-w-[16rem]"
          />
          <span className="text-xs text-gray-400">
            Shows users with this campaign in their review assignments.
          </span>
        </div>
      )}
    </div>
  );
}

interface UserRowProps {
  user: User;
  campaigns: Campaign[];
  isSelf: boolean;
  pending: boolean;
  syncingUnsubscribe: boolean;
  onUpdate: (patch: UserRolePatch) => Promise<void>;
  onSyncUnsubscribeToken: () => Promise<void>;
}

function UserRow({
  user,
  campaigns,
  isSelf,
  pending,
  syncingUnsubscribe,
  onUpdate,
  onSyncUnsubscribeToken,
}: UserRowProps) {
  const reviewIds = user.reviewCampaignIds ?? [];
  const trustedIds = user.trustedCampaignIds ?? [];
  const [confirmSelfDemoteOpen, setConfirmSelfDemoteOpen] = useState(false);
  const missingUnsubscribeToken = !!user.expertId && !user.unsubscribeToken;
  const aiWarning = user.aiDetectionTopWarningQuartile === true;

  const toggleAdmin = () => {
    // Self-demotion is destructive: the moment the write lands, this page
    // (and every other admin-only route) becomes inaccessible until someone
    // else flips the flag back on. Force a confirm so a misclick can't lock
    // the user out.
    if (isSelf && user.isAdmin) {
      setConfirmSelfDemoteOpen(true);
      return;
    }
    void onUpdate({ isAdmin: !user.isAdmin });
  };
  const confirmSelfDemote = () => {
    setConfirmSelfDemoteOpen(false);
    void onUpdate({ isAdmin: false });
  };
  const toggleReviewer = () => {
    const turningOn = !user.isReviewer;
    void onUpdate({
      isReviewer: turningOn,
      // Clear campaign allowlist when demoting.
      ...(turningOn ? {} : { reviewCampaignIds: [] }),
    });
  };

  return (
    <tr className="align-top">
      <td className="px-3 py-2 min-w-0">
        <div className="font-medium text-gray-900 truncate">
          {user.displayName || '(no name)'}
          {isSelf && <span className="ml-2 text-xs text-gray-400">(you)</span>}
        </div>
        <div className="text-xs text-gray-500 truncate">{user.email}</div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {user.isAdmin && <RolePill label="Admin" tone="bg-purple-50 text-purple-800 border-purple-200" />}
          {user.isReviewer && <RolePill label="Reviewer" tone="bg-blue-50 text-blue-800 border-blue-200" />}
          {missingUnsubscribeToken && (
            <RolePill label="No unsub token" tone="bg-amber-50 text-amber-800 border-amber-200" />
          )}
          {aiWarning && <AiDetectionWarningPill />}
          {!user.isAdmin && !user.isReviewer && !missingUnsubscribeToken && !aiWarning && (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        {user.isAdmin ? (
          <span className="text-xs text-gray-400">All campaigns</span>
        ) : user.isReviewer ? (
          <CampaignAssignments
            campaigns={campaigns}
            assignedIds={reviewIds}
            role="reviewer"
            pending={pending}
            onChange={(ids) => onUpdate({ reviewCampaignIds: ids })}
          />
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <CampaignAssignments
          campaigns={campaigns}
          assignedIds={trustedIds}
          role="trusted"
          pending={pending}
          onChange={(ids) => onUpdate({ trustedCampaignIds: ids })}
        />
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center justify-end gap-1 whitespace-nowrap">
          {(pending || syncingUnsubscribe) && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          <SettingsMenu
            user={user}
            pending={pending || syncingUnsubscribe}
            syncingUnsubscribe={syncingUnsubscribe}
            onToggleAdmin={toggleAdmin}
            onToggleReviewer={toggleReviewer}
            onSyncUnsubscribeToken={onSyncUnsubscribeToken}
          />
        </div>
        <ConfirmDialog
          open={confirmSelfDemoteOpen}
          title="Remove your own admin access?"
          message={
            'You are about to revoke your own Admin role. After this you will lose access to the admin tools (including this page) until another admin restores it. Continue?'
          }
          confirmLabel="Remove admin"
          tone="danger"
          busy={pending}
          onCancel={() => setConfirmSelfDemoteOpen(false)}
          onConfirm={confirmSelfDemote}
        />
      </td>
    </tr>
  );
}

function CampaignAssignments({
  campaigns,
  assignedIds,
  role,
  pending,
  onChange,
}: {
  campaigns: Campaign[];
  assignedIds: string[];
  role: 'reviewer' | 'trusted';
  pending: boolean;
  onChange: (ids: string[]) => Promise<void>;
}) {
  const assignedCampaigns = campaigns.filter((campaign) => assignedIds.includes(campaign.id));
  const availableCampaigns = campaigns.filter((campaign) => !assignedIds.includes(campaign.id));

  if (campaigns.length === 0) {
    return <span className="text-xs text-gray-400">No campaigns to assign.</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {assignedCampaigns.map((campaign) => (
        <span
          key={campaign.id}
          className="inline-flex max-w-full items-center rounded-full border border-primary bg-primary text-xs text-white"
        >
          <span className="max-w-[14rem] truncate py-0.5 pl-2 pr-1">{campaign.name}</span>
          <button
            type="button"
            onClick={() => void onChange(assignedIds.filter((id) => id !== campaign.id))}
            disabled={pending}
            aria-label={`Remove ${campaign.name} ${role} assignment`}
            className="mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-50"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      {availableCampaigns.length > 0 && (
        <SearchableDropdown
          options={availableCampaigns.map((campaign) => ({
            value: campaign.id,
            label: campaign.name,
            searchText: `${campaign.name} ${campaign.id}`,
          }))}
          value={null}
          onChange={(id) => {
            if (id && !assignedIds.includes(id)) void onChange([...assignedIds, id]);
          }}
          placeholder="Add Campaign"
          searchPlaceholder="Search campaigns"
          noResultsText="No campaigns found"
          disabled={pending}
          ariaLabel={`Add ${role} campaign`}
          className="!w-auto shrink-0"
          triggerClassName="!w-auto min-h-0 border-0 bg-transparent px-1 py-0.5 text-xs font-medium text-primary shadow-none hover:border-transparent hover:bg-transparent hover:text-primary-hover hover:underline focus:ring-0"
          menuClassName="min-w-64"
          preserveOrder
        />
      )}
    </div>
  );
}

function RolePill({ label, tone }: { label: string; tone: string }) {
  return (
    <span className={`inline-flex h-5 items-center rounded-md border px-1.5 text-[11px] font-medium ${tone}`}>
      {label}
    </span>
  );
}

interface SettingsMenuProps {
  user: User;
  pending: boolean;
  syncingUnsubscribe: boolean;
  onToggleAdmin: () => void;
  onToggleReviewer: () => void;
  onSyncUnsubscribeToken: () => Promise<void>;
}

function SettingsMenu({
  user,
  pending,
  syncingUnsubscribe,
  onToggleAdmin,
  onToggleReviewer,
  onSyncUnsubscribeToken,
}: SettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Reviewer flag is implied by admin (admins can review every campaign),
  // so the menu locks the reviewer toggle while admin is on.
  const reviewerDisabled = pending || !!user.isAdmin;
  const syncDisabled = pending || (!user.expertId && !user.email);
  const browseHref = paths.browseTasks(buildBrowseTasksSearchParams({
    ownerUid: user.uid,
    // User.email is nullable; the filter helper takes `string | undefined`.
    ownerEmail: user.email ?? undefined,
    autorun: true,
  }));

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        title="More actions"
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <PortalMenu
          anchorRef={triggerRef}
          onRequestClose={() => setOpen(false)}
          role="menu"
          className="w-60"
        >
          <MenuToggleItem
            label="Admin"
            checked={!!user.isAdmin}
            disabled={pending}
            onClick={() => {
              onToggleAdmin();
              setOpen(false);
            }}
          />
          <MenuToggleItem
            label="Reviewer"
            checked={!!user.isReviewer}
            disabled={reviewerDisabled}
            onClick={() => {
              onToggleReviewer();
              setOpen(false);
            }}
          />
          <div className="my-1 h-px bg-gray-100" />
          <button
            type="button"
            role="menuitem"
            disabled={syncDisabled}
            onClick={() => {
              void onSyncUnsubscribeToken();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {syncingUnsubscribe ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
            ) : (
              <RefreshCw className="h-4 w-4 text-gray-500" />
            )}
            Sync unsubscribe token
          </button>
          <div className="my-1 h-px bg-gray-100" />
          <Link
            role="menuitem"
            to={browseHref}
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <ListChecks className="h-4 w-4 text-gray-500" />
            Browse this user's tasks
          </Link>
        </PortalMenu>
      )}
    </div>
  );
}

interface MenuToggleItemProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function MenuToggleItem({ label, checked, disabled, onClick }: MenuToggleItemProps) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span>{label}</span>
      <span
        className={`flex h-4 w-4 items-center justify-center rounded border ${
          checked ? 'bg-primary border-primary text-white' : 'border-gray-300 bg-white'
        }`}
      >
        {checked && <Check className="h-3 w-3" />}
      </span>
    </button>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number | null;
  totalCount: number | null;
  pageSize: number;
  currentPageSize: number;
  onNext: () => void;
  onPrev: () => void;
}

function Pagination({
  page,
  totalPages,
  totalCount,
  pageSize,
  currentPageSize,
  onNext,
  onPrev,
}: PaginationProps) {
  if (totalCount !== null && totalCount <= pageSize) return null;
  const start = totalCount === 0 ? 0 : page * pageSize + 1;
  const end = totalCount === null
    ? page * pageSize + currentPageSize
    : Math.min(totalCount, page * pageSize + pageSize);
  const btnClass =
    'px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';
  const nextDisabled = totalPages !== null && page >= totalPages - 1;
  return (
    <div className="mt-4 flex flex-col gap-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
      <div>
        {totalCount === null
          ? `Showing ${start}–${end}`
          : `Showing ${start}–${end} of ${totalCount}`}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:flex">
        <button
          type="button"
          className={btnClass}
          onClick={onPrev}
          disabled={page === 0}
        >
          Previous
        </button>
        <span>
          {totalPages === null
            ? `Page ${page + 1}`
            : `Page ${page + 1} of ${totalPages}`}
        </span>
        <button
          type="button"
          className={btnClass}
          onClick={onNext}
          disabled={nextDisabled}
        >
          Next
        </button>
      </div>
    </div>
  );
}
